/**
 * 光传拓扑组件相关 API。
 *
 * 类型定义与接口路径来源于 Apifox「设计中心」项目。
 */
import { getApiRequest } from '@/utils'
import type { IRes } from '@/types'
import type { AxiosRequestConfig } from 'axios'

const request = getApiRequest()

/** FsCascader 使用的产品分类树节点。 */
export interface OpticalProductCategoryNode {
  /** 分类名称。 */
  label?: string
  /** 分类 ID。 */
  value?: number | string
  /** 子分类。 */
  children?: OpticalProductCategoryNode[]
  /** Apifox 中 TreeInteger 声明的动态节点数据。 */
  key?: Record<string, unknown>
  [key: string]: unknown
}

/** 组件可绑定的产品信息。 */
export interface OpticalProductInfoVO {
  /** 产品 ID。 */
  productId?: number
  /** 产品图片。 */
  productImage?: string
  /** 产品名称。 */
  productName?: string
  /** 产品价格。 */
  productPrice?: string
  /** 产品型号名称。 */
  productModelName?: string
  /** 产品标签列表。 */
  productLabelList?: string[]
}

/** 组件配置的分类与推荐产品。 */
export interface OpticalComponentConfigDetailVO {
  /** 分类菜单。 */
  menus?: OpticalProductCategoryNode[]
  /** 产品列表。 */
  productList?: OpticalProductInfoVO[]
}

/** 查询组件配置参数。 */
export interface GetOpticalComponentConfigParams {
  /** 组件 ID。 */
  id: number
}

/** 搜索组件可绑定产品的请求参数。 */
export interface SearchOpticalComponentProductsDTO {
  /** 每页条数。 */
  size?: number
  /** 当前页数。 */
  current?: number
  /** 搜索内容。 */
  search?: string
  /** 组件 ID。 */
  componentId: number
}

/** 搜索拓扑图可绑定机箱产品的参数。 */
export interface SearchOpticalChassisProductsParams {
  /** 产品名称、型号或 SKU 关键字。 */
  keyword: string
}

/** 分页排序项。 */
export interface OpticalProductOrderItem {
  /** 排序字段。 */
  column?: string
  /** 是否升序。 */
  asc?: boolean
}

/** 产品搜索分页结果。 */
export interface OpticalProductPageVO {
  records?: OpticalProductInfoVO[]
  total?: number
  size?: number
  current?: number
  orders?: OpticalProductOrderItem[]
  optimizeCountSql?: boolean
  searchCount?: boolean
  optimizeJoinOfCountSql?: boolean
  maxLimit?: number
  countId?: string
  pages?: number
}

/** 区域站点绑定机箱产品的请求参数。 */
export interface OpticalAreaBindChassisDTO {
  /** 项目 ID/模板 ID。 */
  id: number
  /** 机箱产品 ID。 */
  productId: number
  /** 区域节点 ID（UUID）。 */
  areaNodeId: string
}

/** 组件绑定机箱产品的请求参数。 */
export interface OpticalComponentBindChassisDTO {
  /** 项目 ID/模板 ID。 */
  id: number
  /** 机箱产品 ID。 */
  productId: number
  /** 组件节点 ID（UUID）。 */
  nodeId: string
  /** 区域节点 ID（UUID）；组件不在区域内时可不传。 */
  areaNodeId?: string
}

/** 查询区域站点已绑定机箱的参数。 */
export interface GetOpticalAreaChassisParams {
  /** 项目 ID/模板 ID。 */
  id: number
  /** 区域节点 ID（UUID）。 */
  areaNodeId: string
}

/** 产品基础信息。 */
export interface OpticalProductBaseInfoVO {
  /** 产品 ID。 */
  productId?: number
  /** 产品图片。 */
  productImage?: string
  /** 产品名称。 */
  productName?: string
  /** 产品型号名称。 */
  productModelName?: string
  /** 已使用槽位数。 */
  usedSlot?: number
  /** 总槽位数。 */
  totalSlot?: number
  /** 产品数量。 */
  quantity?: number
}

/** 查询组件绑定详情的参数。 */
export interface GetOpticalComponentDetailParams {
  /** 项目 ID/模板 ID。 */
  id: number
  /** 组件节点 ID（UUID）。 */
  nodeId: string
  /** 组件绑定的产品 ID。 */
  productId: number
}

/** 产品参数。 */
export interface OpticalProductParameterItem {
  /** 属性名称。 */
  name?: string
  /** 属性描述。 */
  describe?: string
}

/** 带产品参数的产品信息。 */
export interface OpticalProductParameterVO {
  /** 产品 ID。 */
  productId?: number
  /** 产品型号名称。 */
  productModelName?: string
  /** 产品名称。 */
  productName?: string
  /** 产品分类名称。 */
  productCategoryName?: string
  /** 产品图片。 */
  productImage?: string
  /** 产品标签列表。 */
  productLabelList?: string[]
  /** 产品参数列表。 */
  productParameterList?: OpticalProductParameterItem[]
}

/** 组件绑定的产品及其关联机箱详情。 */
export interface OpticalComponentBindProductDetailVO {
  /** 组件绑定的产品。 */
  bindProduct?: OpticalProductParameterVO
  /** 组件关联的机箱产品。 */
  relatedChassis?: OpticalProductParameterVO
}

/** 查询区域站点列表的参数。 */
export interface GetOpticalAreaListParams {
  /** 项目 ID。 */
  projectId: number
}

/** 区域站点信息。 */
export interface OpticalAreaDetailVO {
  /** 区域节点 ID（UUID）。 */
  areaId?: string
  /** 区域节点名称。 */
  areaName?: string
}

/** 查询组件配置的产品分类及产品列表。 */
export function getOpticalComponentConfig(params: GetOpticalComponentConfigParams, config?: AxiosRequestConfig) {
  return request.get<IRes<OpticalComponentConfigDetailVO>>('/design/component/config-list', { ...config, params })
}

/** 搜索组件可绑定的产品列表。 */
export function searchOpticalComponentProducts(data: SearchOpticalComponentProductsDTO, config?: AxiosRequestConfig) {
  return request.post<IRes<OpticalProductPageVO>>('/design/component/search-products', data, config)
}

/** 搜索光传拓扑图可绑定的机箱产品列表。 */
export function searchOpticalChassisProducts(params: SearchOpticalChassisProductsParams, config?: AxiosRequestConfig) {
  return request.get<IRes<OpticalProductInfoVO[]>>('/design/topology/product/search-chassis', { ...config, params })
}

/** 区域站点绑定机箱产品。 */
export function bindOpticalAreaChassis(data: OpticalAreaBindChassisDTO, config?: AxiosRequestConfig) {
  return request.post<IRes<boolean>>('/design/topology/area/bind-chassis', data, config)
}

/** 组件绑定机箱产品。 */
export function bindOpticalComponentChassis(data: OpticalComponentBindChassisDTO, config?: AxiosRequestConfig) {
  return request.post<IRes<boolean>>('/design/topology/component/bind-chassis', data, config)
}

/** 查询区域站点已绑定的机箱产品。 */
export function getOpticalAreaChassis(params: GetOpticalAreaChassisParams, config?: AxiosRequestConfig) {
  return request.get<IRes<OpticalProductBaseInfoVO[]>>('/design/topology/area/chassis', { ...config, params })
}

/** 查询组件绑定的产品、关联机箱和产品参数。 */
export function getOpticalComponentDetail(params: GetOpticalComponentDetailParams, config?: AxiosRequestConfig) {
  return request.get<IRes<OpticalComponentBindProductDetailVO>>('/design/topology/component/detail', {
    ...config,
    params,
  })
}

/** 查询项目中的区域站点下拉列表。 */
export function getOpticalAreaList(params: GetOpticalAreaListParams, config?: AxiosRequestConfig) {
  return request.get<IRes<OpticalAreaDetailVO[]>>('/design/topology/area-list', { ...config, params })
}
